// dashboard/modals/FinalizePatientModal.js
// Restricted to clinic managers or the vet assigned to this condition (see
// canFinalize in PatientDetailPanel).
//
// 2026-08-31, Bryan: a clinic must never be able to unilaterally end
// monitoring for a pet — a mistaken or malicious click here used to
// immediately finalize deceased/euthanized with no owner recourse. This
// modal no longer finalizes anything; it calls request_pet_finalization,
// which pauses the pet's monitoring and asks the OWNER to independently
// pick the real reason and confirm (or dismiss) from their own device.
// Transferred/inactive/unknown/client_requested are out of scope here —
// a clinic never sets a pet's outcome directly for those either; the
// self-service mute/de-link replacement for them is a separate, later
// piece of work. Until that ships, this modal only offers the two
// outcomes a clinic can legitimately need to flag urgently.
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { c } from '../constants';
import { useModalA11y } from '../useModalA11y';

export default function FinalizePatientModal({ patient, onConfirm, onCancel, loading, error }) {
  const { t } = useTranslation();
  const [outcomeType, setOutcomeType] = useState('deceased');
  const [reasonDetail, setReasonDetail] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const containerRef = useModalA11y(onCancel);

  const outcomes = [
    { value: 'deceased',   label: t('outcomeType.deceased') },
    { value: 'euthanized', label: t('outcomeType.euthanized') },
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(79,100,115,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
      <div
        ref={containerRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="finalize-modal-title"
        style={{ backgroundColor: c.white, borderRadius: 16, padding: 28, width: '100%', maxWidth: 440, boxShadow: '0 8px 32px rgba(79,100,115,0.18)', outline: 'none' }}
      >
        <h2 id="finalize-modal-title" style={{ fontSize: 17, fontWeight: 600, color: c.slate, marginBottom: 4 }}>{t('finalizeModal.title', { petName: patient.pet.name })}</h2>
        <p style={{ fontSize: 12, color: c.textSoft, marginBottom: 20 }}>
          {t('finalizeModal.body', { petName: patient.pet.name })}
        </p>

        <label htmlFor="finalize-outcome" style={{ display: 'block', fontSize: 12, fontWeight: 500, color: c.slate, marginBottom: 6 }}>{t('finalizeModal.reasonLabel')}</label>
        <select
          id="finalize-outcome"
          style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${c.border}`, backgroundColor: c.fog, fontSize: 13, marginBottom: 14 }}
          value={outcomeType}
          onChange={e => { setOutcomeType(e.target.value); setConfirmed(false); }}
        >
          {outcomes.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>

        <label htmlFor="finalize-detail" style={{ display: 'block', fontSize: 12, fontWeight: 500, color: c.slate, marginBottom: 6 }}>{t('finalizeModal.notesLabel')} <span style={{ fontWeight: 400 }}>{t('finalizeModal.optional')}</span></label>
        <textarea
          id="finalize-detail"
          value={reasonDetail}
          onChange={e => setReasonDetail(e.target.value)}
          rows={2}
          style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${c.border}`, backgroundColor: c.fog, fontSize: 13, marginBottom: 14, resize: 'vertical', fontFamily: 'inherit' }}
        />

        <p style={{ fontSize: 12, color: c.textSoft, backgroundColor: c.fog, borderRadius: 8, padding: '10px 12px', marginBottom: 14, lineHeight: 1.5 }}>
          {t('finalizeModal.severeOutcomeNote', { petName: patient.pet.name })}
        </p>

        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: c.slate, marginBottom: 18, cursor: 'pointer' }}>
          <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} style={{ marginTop: 2 }} />
          {t('finalizeModal.confirmCheckboxSevere', { petName: patient.pet.name })}
        </label>

        {error && <p role="alert" style={{ fontSize: 12, color: c.error, marginBottom: 12 }}>{error}</p>}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{ padding: '8px 16px', background: 'none', border: `1px solid ${c.border}`, borderRadius: 8, fontSize: 13, cursor: 'pointer', color: c.textSoft }}>{t('common.cancel')}</button>
          <button
            onClick={() => confirmed && onConfirm({ outcomeType, reasonDetail })}
            disabled={!confirmed || loading}
            style={{ padding: '8px 16px', backgroundColor: c.red, color: c.white, border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: confirmed ? 'pointer' : 'not-allowed', opacity: confirmed ? 1 : 0.5 }}
          >
            {loading ? t('finalizeModal.finalizing') : t('finalizeModal.finalizeButton')}
          </button>
        </div>
      </div>
    </div>
  );
}
